/**
 * Mouse Event Normalizer Service
 * Handles mouse event capturing and muting for touch/pointer event normalization.
 *
 * @module core-v2/services/mouse-event-normalizer.service
 */

import type { ISupportService } from "../models/support";

/**
 * Mouse event normalizer service for handling touch/mouse event conflicts.
 * Prevents ghost clicks and normalizes touch events to mouse events.
 */
export class MouseEventNormalizerService {
    /**
     * Whether the mouse trap has been set up
     */
    mouseTrap: boolean = false;

    /**
     * Whether click events should be busted (prevented)
     */
    bustClick: boolean = false;

    /**
     * Whether mouse events are currently being captured/muted
     */
    captureMouse: boolean = false;

    /**
     * Timeout ID for the mouse trap release
     */
    mouseTrapTimeoutID: ReturnType<typeof setTimeout> | undefined;

    /**
     * List of mouse events to capture
     */
    private readonly MOUSE_EVENTS = ["mousedown", "mousemove", "mouseenter", "mouseleave", "mouseover", "mouseout", "mouseup", "click"];

    /**
     * Selector for elements that should not have click busting
     */
    private readonly EXCLUDE_BUST_CLICK_SELECTOR = "label, input, [data-rel=external]";

    constructor(
        private $: JQueryStatic,
        private support: ISupportService
    ) {}

    /**
     * Set up mouse event capturing to prevent ghost clicks from touch events.
     * This sets up event listeners on document.documentElement to intercept
     * and optionally stop mouse events when touch events are active.
     */
    setupMouseMute(): void {
        let idx = 0;
        const length = this.MOUSE_EVENTS.length;
        const element = document.documentElement;

        if (this.mouseTrap || !this.support.eventCapture) {
            return;
        }

        this.mouseTrap = true;

        this.bustClick = false;
        this.captureMouse = false;

        const self = this;
        const handler = function(e: Event) {
            if (self.captureMouse) {
                if (e.type === "click") {
                    if (self.bustClick && !self.$(e.target as Element).is(self.EXCLUDE_BUST_CLICK_SELECTOR)) {
                        e.preventDefault();
                        e.stopPropagation();
                    }
                } else {
                    e.stopPropagation();
                }
            }
        };

        for (; idx < length; idx++) {
            element.addEventListener(this.MOUSE_EVENTS[idx], handler, true);
        }
    }

    /**
     * Mute mouse events. Called on touchstart to prevent ghost clicks.
     * @param e - The jQuery event with bustClick data
     */
    muteMouse(e: JQuery.Event & { data?: { bustClick?: boolean } }): void {
        this.captureMouse = true;
        if (e.data?.bustClick) {
            this.bustClick = true;
        }
        clearTimeout(this.mouseTrapTimeoutID);
    }

    /**
     * Unmute mouse events. Called on touchend after a delay to allow
     * legitimate mouse events through again.
     */
    unMuteMouse(): void {
        clearTimeout(this.mouseTrapTimeoutID);
        this.mouseTrapTimeoutID = setTimeout(() => {
            this.captureMouse = false;
            this.bustClick = false;
        }, 400);
    }
}
